import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
    collection, getDocs, updateDoc, doc, deleteDoc,
    orderBy, query, addDoc, serverTimestamp, runTransaction, where
} from "firebase/firestore";
import { FiEye, FiX, FiTruck, FiTrash2, FiEdit2, FiCheck, FiDollarSign, FiRefreshCw, FiPlus, FiSearch, FiMail } from "react-icons/fi";
import toast from "react-hot-toast";
import { getDoc } from "firebase/firestore";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { sendOrderConfirmation } from "../../utils/email";

const STATUS_OPTIONS = ["pending", "pending_ath_verification", "paid", "processing", "shipped", "delivered", "cancelled"];
const MANUAL_PAYMENT_METHODS = ["Manual (Admin)", "ATH Móvil", "Cash", "Bank Transfer", "Other"];

const STATUS_COLORS = {
    pending:                  { bg: "#fff3cd", color: "#856404" },
    pending_ath_verification: { bg: "#fff5eb", color: "#d9480f" },
    paid:                     { bg: "#d4edda", color: "#155724" },
    processing:               { bg: "#cce5ff", color: "#004085" },
    shipped:                  { bg: "#d1ecf1", color: "#0c5460" },
    delivered:                { bg: "#d4edda", color: "#155724" },
    cancelled:                { bg: "#f8d7da", color: "#721c24" },
};

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewing, setViewing] = useState(null);
    const [tracking, setTracking] = useState("");
    const [updatingTracking, setUpdatingTracking] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [deleting, setDeleting] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [payConfig, setPayConfig] = useState(null);
    const [approving, setApproving] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(null); // stores orderId
    const [selectedItems, setSelectedItems] = useState([]); // indices of items for new shipment
    const [shipmentTracking, setShipmentTracking] = useState("");
    const [sendingShippedEmail, setSendingShippedEmail] = useState(false);
    const [oneTimeTokens, setOneTimeTokens] = useState({});
    const [generatingToken, setGeneratingToken] = useState(null);

    // Manual Order State
    const [creating, setCreating] = useState(false);
    const [allProducts, setAllProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [editSearchTerm, setEditSearchTerm] = useState("");
    const [dateFilter, setDateFilter] = useState("all"); // all, today, month, last_month, custom
    const [customRange, setCustomRange] = useState({ start: "", end: "" });
    const [manualForm, setManualForm] = useState({
        customerName: "", email: "", phone: "",
        requiresEmail: true,
        requiresShipping: true,
        address: "", shippingLine1: "", shippingCity: "", shippingState: "", shippingZip: "", shippingCountry: "Puerto Rico",
        items: [],
        status: "paid",
        paymentMethod: "Manual (Admin)",
        subtotal: 0,
        shippingCost: 0,
        total: 0
    });

    async function loadOrders() {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, "orders"));
            const all = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => {
                    const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
                    const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
                    return db2 - da;
                });
            setOrders(all);
        } catch {
            toast.error("Could not load orders.");
        }
        setLoading(false);
    }

    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const filteredOrders = orders.filter(o => {
        // Text search
        const s = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm || (
            o.id.toLowerCase().includes(s) ||
            o.customerName?.toLowerCase().includes(s) ||
            o.email?.toLowerCase().includes(s)
        );
        if (!matchesSearch) return false;

        // Date filter
        const orderDate = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(0);
        if (dateFilter === "today") {
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            return orderDate >= startOfToday;
        }
        if (dateFilter === "month") {
            const startOfMonth = new Date();
            startOfMonth.setDate(1);
            startOfMonth.setHours(0, 0, 0, 0);
            return orderDate >= startOfMonth;
        }
        if (dateFilter === "last_month") {
            const startOfLastMonth = new Date();
            startOfLastMonth.setMonth(startOfLastMonth.getMonth() - 1);
            startOfLastMonth.setDate(1);
            startOfLastMonth.setHours(0, 0, 0, 0);
            const endOfLastMonth = new Date();
            endOfLastMonth.setDate(0);
            endOfLastMonth.setHours(23, 59, 59, 999);
            return orderDate >= startOfLastMonth && orderDate <= endOfLastMonth;
        }
        if (dateFilter === "custom" && customRange.start && customRange.end) {
            const s = new Date(customRange.start);
            s.setHours(0, 0, 0, 0);
            const e = new Date(customRange.end);
            e.setHours(23, 59, 59, 999);
            return orderDate >= s && orderDate <= e;
        }
        return true;
    });

    const newOrders = filteredOrders.filter(o => {
        const d = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(0);
        return d > oneDayAgo;
    });

    const otherOrders = filteredOrders.filter(o => {
        const d = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(0);
        return d <= oneDayAgo;
    });

    useEffect(() => { 
        loadOrders(); 
        async function loadPay() {
            const snap = await getDoc(doc(db, "siteConfig", "payments"));
            if (snap.exists()) setPayConfig(snap.data());
        }
        async function loadProducts() {
            const snap = await getDocs(collection(db, "products"));
            setAllProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
        loadPay();
        loadProducts();
    }, []);

    async function updateStatus(id, status) {
        try {
            await updateDoc(doc(db, "orders", id), { status, updatedAt: serverTimestamp() });
            setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
            if (viewing?.id === id) setViewing(v => ({ ...v, status }));
            toast.success("Status updated!");
        } catch {
            toast.error("Failed to update status.");
        }
    }

    async function saveTracking() {
        if (!viewing) return;
        setUpdatingTracking(true);
        try {
            const carrierToSave = editedData.carrier || viewing.carrier || "USPS";
            await updateDoc(doc(db, "orders", viewing.id), {
                trackingNumber: tracking,
                carrier: carrierToSave,
                trackingUpdatedAt: serverTimestamp(),
                status: tracking ? "shipped" : viewing.status,
            });
            setOrders(prev => prev.map(o => o.id === viewing.id ? { ...o, trackingNumber: tracking, carrier: carrierToSave, status: tracking ? "shipped" : o.status } : o));
            setViewing(v => ({ ...v, trackingNumber: tracking, carrier: carrierToSave, status: tracking ? "shipped" : v.status }));
            toast.success("Tracking saved!");
        } catch {
            toast.error("Failed to save tracking.");
        }
        setUpdatingTracking(false);
    }

    async function addShipment() {
        if (!viewing || !shipmentTracking || selectedItems.length === 0) {
            toast.error("Please select items and enter a tracking number.");
            return;
        }
        setUpdatingTracking(true);
        try {
            const newShipment = {
                id: Math.random().toString(36).substring(2, 9),
                trackingNumber: shipmentTracking,
                carrier: editedData.carrier || "USPS",
                items: selectedItems.map(idx => ({
                    title: viewing.items[idx].title,
                    quantity: viewing.items[idx].quantity,
                    imageUrl: viewing.items[idx].imageUrl || null,
                    itemIndex: idx // Store original index to track which items are shipped
                })),
                shippedAt: new Date().toISOString()
            };

            const updatedShipments = [...(viewing.shipments || []), newShipment];
            
            await updateDoc(doc(db, "orders", viewing.id), {
                shipments: updatedShipments,
                status: "shipped",
                updatedAt: serverTimestamp()
            });

            setOrders(prev => prev.map(o => o.id === viewing.id ? { ...o, shipments: updatedShipments, status: "shipped" } : o));
            setViewing(v => ({ ...v, shipments: updatedShipments, status: "shipped" }));
            setShipmentTracking("");
            setSelectedItems([]);
            toast.success("Shipment added!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to add shipment.");
        }
        setUpdatingTracking(false);
    }

    async function deleteShipment(shipmentId) {
        if (!window.confirm("Remove this shipment?")) return;
        try {
            const updatedShipments = viewing.shipments.filter(s => s.id !== shipmentId);
            await updateDoc(doc(db, "orders", viewing.id), {
                shipments: updatedShipments,
                updatedAt: serverTimestamp()
            });
            setOrders(prev => prev.map(o => o.id === viewing.id ? { ...o, shipments: updatedShipments } : o));
            setViewing(v => ({ ...v, shipments: updatedShipments }));
            toast.success("Shipment removed.");
        } catch {
            toast.error("Failed to remove shipment.");
        }
    }

    async function handleDelete(id) {
        if (deleting) return;
        setDeleting(true);
        console.log("DELETING_ORDER:", id);
        try {
            await deleteDoc(doc(db, "orders", id));
            setOrders(prev => prev.filter(o => o.id !== id));
            if (viewing?.id === id) setViewing(null);
            setDeleteConfirmId(null);
            toast.success("Order deleted.");
        } catch (err) {
            console.error("DELETE_FAILED:", err);
            toast.error(`Failed to delete order: ${err.message || "Unknown error"}`);
        }
        setDeleting(false);
    }

    async function saveCustomerInfo() {
        if (!viewing) return;
        try {
            const hasDigital = editedData.items?.some(i => i.isDigital || i.bundleDelivery === "Combo");
            const wasPaid = ["paid", "processing", "shipped", "delivered"].includes(viewing.status);
            const updatePayload = { ...editedData, updatedAt: serverTimestamp() };
            
            if (hasDigital && wasPaid && !viewing.downloadExpiresAt) {
                updatePayload.downloadExpiresAt = Date.now() + 1 * 60 * 60 * 1000; // 1 hour
            }
            
            await updateDoc(doc(db, "orders", viewing.id), updatePayload);
            setOrders(prev => prev.map(o => o.id === viewing.id ? { ...o, ...updatePayload } : o));
            setViewing(v => ({ ...v, ...updatePayload }));
            setIsEditing(false);
            toast.success("Order updated successfully!");
        } catch {
            toast.error("Failed to update order.");
        }
    }

    async function approvePayment(order) {
        if (!window.confirm("Confirm payment received? This will mark the order as PAID and send a confirmation email.")) return;
        setApproving(true);
        try {
            await updateDoc(doc(db, "orders", order.id), { status: "paid", updatedAt: serverTimestamp() });
            
            // Trigger email if enabled
            if (payConfig?.emailEnabled) {
                await sendOrderConfirmation(order, payConfig);
                toast.success("Payment approved and email sent!");
            } else {
                toast.success("Payment approved!");
            }

            setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: "paid" } : o));
            setViewing(v => v?.id === order.id ? { ...v, status: "paid" } : v);
        } catch (e) {
            console.error(e);
            toast.error("Approval failed.");
        }
        setApproving(false);
    }

    async function resetDownloadWindow(order) {
        if (!window.confirm("Reset download window for another 1 hour?")) return;
        try {
            const newExpiry = Date.now() + 1 * 60 * 60 * 1000; // 1 hour
            await updateDoc(doc(db, "orders", order.id), { 
                downloadExpiresAt: newExpiry,
                updatedAt: serverTimestamp() 
            });
            setOrders(prev => prev.map(o => o.id === order.id ? { ...o, downloadExpiresAt: newExpiry } : o));
            setViewing(v => v?.id === order.id ? { ...v, downloadExpiresAt: newExpiry } : v);
            toast.success("Download window reset for 1 hour!");
        } catch {
            toast.error("Failed to reset window.");
        }
    }

    async function handleResendEmail(orderId) {
        setResendingEmail(orderId);
        try {
            const resendFn = httpsCallable(functions, 'resendOrderEmail');
            await resendFn({ orderId });
            toast.success("Confirmation email resent!");
        } catch (error) {
            console.error("Resend failed:", error);
            toast.error("Failed to resend email.");
        } finally {
            setResendingEmail(null);
        }
    }

    async function handleSendShippedEmail(orderId, shipment = null) {
        if (!window.confirm("Send a shipping confirmation email to the customer?")) return;
        setSendingShippedEmail(true);
        try {
            const sendFn = httpsCallable(functions, 'sendShippedEmail');
            const payload = { orderId };
            if (shipment) {
                payload.shipmentId = shipment.id;
                payload.carrier = shipment.carrier;
                payload.trackingNumber = shipment.trackingNumber;
                payload.items = shipment.items;
            } else {
                payload.carrier = viewing.carrier || editedData.carrier || "USPS";
                payload.trackingNumber = viewing.trackingNumber;
            }
            await sendFn(payload);
            toast.success("Shipped email sent successfully!");
        } catch (error) {
            console.error("Failed to send shipped email:", error);
            toast.error("Failed to send shipped email.");
        } finally {
            setSendingShippedEmail(false);
        }
    }

    async function handleGenerateOneTimeQR(item, index, resolvedPath) {
        const key = `${item.id}_${index}`;
        setGeneratingToken(key);
        try {
            const generateFn = httpsCallable(functions, "generateOneTimeDownload");
            const res = await generateFn({
                orderId: viewing.id,
                itemId: item.id || null,
                title: item.title,
                epubStoragePath: resolvedPath
            });
            if (res.data?.success && res.data?.tokenId) {
                setOneTimeTokens(prev => ({ ...prev, [key]: res.data.tokenId }));
                toast.success("One-time download link generated!");
            } else {
                toast.error("Failed to generate download link.");
            }
        } catch (error) {
            console.error("Failed to generate OTD token:", error);
            toast.error(error.message || "Failed to generate download link.");
        } finally {
            setGeneratingToken(null);
        }
    }

    function startEditing() {
        setEditedData({
            customerName: viewing.customerName || "",
            email: viewing.email || "",
            phone: viewing.phone || "",
            address: viewing.address || "",
            shippingLine1: viewing.shippingLine1 || "",
            shippingCity: viewing.shippingCity || "",
            shippingState: viewing.shippingState || "",
            shippingZip: viewing.shippingZip || "",
            shippingCountry: viewing.shippingCountry || "",
            carrier: viewing.carrier || "USPS",
            items: viewing.items ? JSON.parse(JSON.stringify(viewing.items)) : [],
            shippingCost: viewing.shippingCost || 0,
            subtotal: viewing.subtotal || 0,
            total: viewing.total || 0
        });
        setIsEditing(true);
    }

    const totalRevenue = orders
        .filter(o => ["paid","processing","shipped","delivered"].includes(o.status))
        .reduce((s, o) => s + (o.total || 0), 0);

    const salesToday = orders
        .filter(o => {
            const d = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(0);
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            return d >= startOfToday && ["paid","processing","shipped","delivered"].includes(o.status);
        })
        .reduce((s, o) => s + (o.total || 0), 0);

    const salesThisMonth = orders
        .filter(o => {
            const d = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(0);
            const startOfMonth = new Date();
            startOfMonth.setDate(1);
            startOfMonth.setHours(0, 0, 0, 0);
            return d >= startOfMonth && ["paid","processing","shipped","delivered"].includes(o.status);
        })
        .reduce((s, o) => s + (o.total || 0), 0);

    async function handleCreateManualOrder() {
        if (!manualForm.customerName || (manualForm.requiresEmail && !manualForm.email) || manualForm.items.length === 0) {
            toast.error(`Please fill Name, ${manualForm.requiresEmail ? "Email, " : ""}and add at least one item.`);
            return;
        }

        const hasDigital = manualForm.items.some(i => i.isDigital);
        if (hasDigital && !manualForm.email) {
            if (!window.confirm("This order contains digital items but no email. The customer won't receive their download link via email. Continue?")) return;
        }
        
        setLoading(true);
        const counterRef = doc(db, "counters", "orders");
        
        try {
            const orderId = await runTransaction(db, async (transaction) => {
                const counterSnap = await transaction.get(counterRef);
                let nextId = 1001;
                if (counterSnap.exists()) {
                    nextId = counterSnap.data().lastId + 1;
                }
                
                const newOrderId = String(nextId);
                const orderRef = doc(db, "orders", newOrderId);
                
                const orderData = {
                    ...manualForm,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    // Digital expiry if any
                    ...(manualForm.items.some(i => i.isDigital) && { downloadExpiresAt: Date.now() + 48 * 60 * 60 * 1000 })
                };
                
                transaction.set(orderRef, orderData);
                transaction.set(counterRef, { lastId: nextId }, { merge: true });
                return newOrderId;
            });

            toast.success(`Order #${orderId} created!`);
            const link = `${window.location.origin}/order/${orderId}`;
            window.prompt("Manual Order Link (Copy to share with customer):", link);
            
            setCreating(false);
            setManualForm({
                customerName: "", email: "", phone: "",
                requiresEmail: true,
                requiresShipping: true,
                address: "", shippingLine1: "", shippingCity: "", shippingState: "", shippingZip: "", shippingCountry: "Puerto Rico",
                items: [],
                status: "paid",
                paymentMethod: "Manual (Admin)",
                subtotal: 0,
                shippingCost: 0,
                total: 0
            });
            loadOrders();
        } catch (e) {
            console.error(e);
            toast.error("Failed to create manual order.");
        }
        setLoading(false);
    }

    function addManualItem(p) {
        const hasVariants = p.variants && p.variants.length > 0;
        const defaultVariant = hasVariants ? p.variants[0] : null;

        const newItem = {
            id: p.id,
            title: p.title,
            price: defaultVariant ? defaultVariant.price : (p.price || 0),
            quantity: 1,
            imageUrl: p.imageUrl || null,
            isDigital: defaultVariant 
                ? (defaultVariant.format === "eBook" || defaultVariant.format === "Audiobook")
                : (p.isDigital || false),
            format: defaultVariant ? defaultVariant.format : (p.format || "Paperback"),
            epubStoragePath: defaultVariant ? (defaultVariant.epubStoragePath || null) : (p.epubStoragePath || null)
        };
        const newItems = [...manualForm.items, newItem];
        updateTotals(newItems, manualForm.shippingCost);
    }

    function updateTotals(items, shippingCost) {
        const sub = items.reduce((s, i) => s + (i.price * i.quantity), 0);
        const total = sub + (parseFloat(shippingCost) || 0);
        setManualForm(prev => ({ ...prev, items, subtotal: sub, shippingCost: parseFloat(shippingCost) || 0, total }));
    }

    function addEditManualItem(p) {
        const hasVariants = p.variants && p.variants.length > 0;
        const defaultVariant = hasVariants ? p.variants[0] : null;

        const newItem = {
            id: p.id,
            title: p.title,
            price: defaultVariant ? defaultVariant.price : (p.price || 0),
            quantity: 1,
            imageUrl: p.imageUrl || null,
            isDigital: defaultVariant 
                ? (defaultVariant.format === "eBook" || defaultVariant.format === "Audiobook")
                : (p.isDigital || false),
            format: defaultVariant ? defaultVariant.format : (p.format || "Paperback"),
            epubStoragePath: defaultVariant ? (defaultVariant.epubStoragePath || null) : (p.epubStoragePath || null)
        };
        const newItems = [...(editedData.items || []), newItem];
        updateEditTotals(newItems, editedData.shippingCost);
    }

    function updateEditTotals(items, shippingCost) {
        const sub = items.reduce((s, i) => s + (i.price * i.quantity), 0);
        const discount = viewing?.discountAmount || 0;
        const total = Math.max(0, sub + (parseFloat(shippingCost) || 0) - discount);
        setEditedData(prev => ({ ...prev, items, subtotal: sub, shippingCost: parseFloat(shippingCost) || 0, total }));
    }

    const filteredProducts = searchTerm 
        ? allProducts.filter(p => p.title?.toLowerCase().includes(searchTerm.toLowerCase()))
        : [];

    const filteredEditProducts = editSearchTerm 
        ? allProducts.filter(p => p.title?.toLowerCase().includes(editSearchTerm.toLowerCase()))
        : [];

    return (
        <>
            {newOrders.length > 0 && (
                <div className="admin-activity-banner">
                    <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
                        <div style={{ width: 45, height: 45, background: "rgba(255,255,255,0.15)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiDollarSign size={22} color="#c2a35d" />
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontSize: 16 }}>New activity detected!</h4>
                            <p style={{ margin: 0, fontSize: 13, opacity: 0.85 }}>You have {newOrders.length} order{newOrders.length > 1 ? 's' : ''} in the last 24 hours.</p>
                        </div>
                    </div>
                    <button className="admin-btn-primary" style={{ background: "#c2a35d", border: "none" }} onClick={() => {
                        const el = document.getElementById('new-orders-section');
                        el?.scrollIntoView({ behavior: 'smooth' });
                    }}>
                        View New Orders
                    </button>
                </div>
            )}

            <div className="admin-stats">
                <div className="admin-stat-card">
                    <div className="stat-label">Total Revenue</div>
                    <div className="stat-value" style={{ color: "#1a6b4a" }}>${totalRevenue.toFixed(2)}</div>
                </div>
                <div className="admin-stat-card" style={{ borderLeft: "4px solid #c2a35d" }}>
                    <div className="stat-label">Sales Today</div>
                    <div className="stat-value">${salesToday.toFixed(2)}</div>
                </div>
                <div className="admin-stat-card" style={{ borderLeft: "4px solid #c2a35d" }}>
                    <div className="stat-label">Sales This Month</div>
                    <div className="stat-value">${salesThisMonth.toFixed(2)}</div>
                </div>
                <div className="admin-stat-card">
                    <div className="stat-label">Total Orders</div>
                    <div className="stat-value">{orders.length}</div>
                </div>
                <div className="admin-stat-card">
                    <div className="stat-label">Pending Approval</div>
                    <div className="stat-value" style={{ color: "#d9480f" }}>
                        {orders.filter(o => o.status === "pending" || o.status === "pending_ath_verification").length}
                    </div>
                </div>
            </div>

            <div className="admin-section-header">
                <h3>Order Management</h3>
                <div className="admin-action-row">
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1, minWidth: 200 }}>
                        <select 
                            value={dateFilter} 
                            onChange={(e) => setDateFilter(e.target.value)}
                            style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 14, background: "white" }}
                        >
                            <option value="all">All Time</option>
                            <option value="today">Today</option>
                            <option value="month">This Month</option>
                            <option value="last_month">Last Month</option>
                            <option value="custom">Custom Range</option>
                        </select>

                        {dateFilter === "custom" && (
                            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                                <input 
                                    type="date" 
                                    value={customRange.start} 
                                    onChange={e => setCustomRange({...customRange, start: e.target.value})}
                                    style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 13 }}
                                />
                                <span style={{ fontSize: 12, color: "#888" }}>to</span>
                                <input 
                                    type="date" 
                                    value={customRange.end} 
                                    onChange={e => setCustomRange({...customRange, end: e.target.value})}
                                    style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 13 }}
                                />
                            </div>
                        )}
                    </div>
                    <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                        <FiSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#888" }} />
                        <input 
                            type="text" 
                            placeholder="Search orders..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ padding: "8px 12px 8px 35px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 14, width: "100%" }}
                        />
                    </div>
                    <button className="admin-btn-primary" onClick={() => setCreating(true)}>
                        <FiPlus size={16} /> Create Manual Order
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="admin-loading">Loading orders…</div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
                    
                    {/* New Orders Section */}
                    {newOrders.length > 0 && (
                        <div id="new-orders-section">
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 15 }}>
                                <div style={{ width: 8, height: 8, background: "#c2a35d", borderRadius: "50%" }}></div>
                                <h4 style={{ margin: 0, color: "#0d2a1e", textTransform: "uppercase", letterSpacing: 1, fontSize: 13 }}>New Orders (Last 24 Hours)</h4>
                            </div>
                            <div className="admin-card">
                                <OrderTable 
                                    list={newOrders} 
                                    updateStatus={updateStatus}
                                    setViewing={setViewing}
                                    setTracking={setTracking}
                                    setIsEditing={setIsEditing}
                                    handleDelete={handleDelete}
                                    deleting={deleting}
                                    deleteConfirmId={deleteConfirmId}
                                    setDeleteConfirmId={setDeleteConfirmId}
                                    STATUS_COLORS={STATUS_COLORS}
                                    STATUS_OPTIONS={STATUS_OPTIONS}
                                    handleResendEmail={handleResendEmail}
                                    resendingEmail={resendingEmail}
                                />
                            </div>
                        </div>
                    )}

                    {/* All Orders Section */}
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 15 }}>
                            <div style={{ width: 8, height: 8, background: "#888", borderRadius: "50%" }}></div>
                            <h4 style={{ margin: 0, color: "#888", textTransform: "uppercase", letterSpacing: 1, fontSize: 13 }}>
                                {searchTerm ? "Search Results" : "All Orders"}
                            </h4>
                        </div>
                        <div className="admin-card">
                            {otherOrders.length === 0 && !searchTerm ? (
                                <div className="admin-empty"><p>No historical orders found.</p></div>
                            ) : (
                                <OrderTable 
                                    list={otherOrders} 
                                    updateStatus={updateStatus}
                                    setViewing={setViewing}
                                    setTracking={setTracking}
                                    setIsEditing={setIsEditing}
                                    handleDelete={handleDelete}
                                    deleting={deleting}
                                    deleteConfirmId={deleteConfirmId}
                                    setDeleteConfirmId={setDeleteConfirmId}
                                    STATUS_COLORS={STATUS_COLORS}
                                    STATUS_OPTIONS={STATUS_OPTIONS}
                                    handleResendEmail={handleResendEmail}
                                    resendingEmail={resendingEmail}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Order Detail Modal */}
            {viewing && (
                <div className="admin-modal-backdrop" onClick={e => e.target === e.currentTarget && setViewing(null)}>
                    <div className="admin-modal" style={{ maxWidth: 700 }}>
                        <div className="admin-modal-header" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, width: "100%" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                                <h3 style={{ margin: 0 }}>Order #{viewing.id}</h3>
                                <button className="admin-modal-close" onClick={() => setViewing(null)}><FiX size={20} /></button>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "#f8f9fa", padding: "8px 12px", borderRadius: 8, border: "1px solid #e9ecef", fontSize: 13 }}>
                                <span style={{ fontWeight: 700, color: "#495057" }}>Customer Link:</span>
                                <a 
                                    href={`${window.location.origin}/order/${viewing.id}`} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    style={{ color: "var(--accent)", textDecoration: "underline", wordBreak: "break-all" }}
                                >
                                    {window.location.origin}/order/{viewing.id}
                                </a>
                                <button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${window.location.origin}/order/${viewing.id}`);
                                        toast.success("Order link copied!");
                                    }}
                                    style={{
                                        marginLeft: "auto",
                                        background: "#e9ecef",
                                        border: "none",
                                        borderRadius: 4,
                                        padding: "4px 8px",
                                        fontSize: 11,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        color: "#495057"
                                    }}
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-grid-2" style={{ marginBottom: 24 }}>
                                <div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                        <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", margin: 0 }}>Customer</p>
                                        {!isEditing && <button onClick={startEditing} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>EDIT INFO</button>}
                                    </div>
                                    {isEditing ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            <input value={editedData.customerName} onChange={e => setEditedData({...editedData, customerName: e.target.value})} placeholder="Name" style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6 }} />
                                            <input value={editedData.email} onChange={e => setEditedData({...editedData, email: e.target.value})} placeholder="Email" style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6 }} />
                                            <input value={editedData.phone} onChange={e => setEditedData({...editedData, phone: e.target.value})} placeholder="Phone" style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6 }} />
                                        </div>
                                    ) : (
                                        <>
                                            <p style={{ margin: 0 }}><strong>{viewing.customerName || "—"}</strong></p>
                                            <p style={{ fontSize: 13, color: "#666", margin: 0 }}>{viewing.email}</p>
                                            <p style={{ fontSize: 13, color: "#666", margin: 0 }}>{viewing.phone}</p>
                                        </>
                                    )}
                                </div>
                                <div>
                                    <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Status</p>
                                    <select
                                        value={viewing.status || "pending"}
                                        onChange={e => updateStatus(viewing.id, e.target.value)}
                                        style={{
                                            padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e0ddd8",
                                            fontSize: 13, fontWeight: 700, outline: "none", cursor: "pointer",
                                            background: STATUS_COLORS[viewing.status]?.bg || "#f4f5f7",
                                            color: STATUS_COLORS[viewing.status]?.color || "#333",
                                        }}
                                    >
                                        {STATUS_OPTIONS.map(s => (
                                            <option key={s} value={s}>{s.replace(/_/g, ' ').charAt(0).toUpperCase() + s.replace(/_/g, ' ').slice(1)}</option>
                                        ))}
                                    </select>
                                    {viewing.status === "pending_ath_verification" && (
                                        <button 
                                            className="admin-btn-primary" 
                                            onClick={() => approvePayment(viewing)}
                                            disabled={approving}
                                            style={{ marginTop: 12, width: "100%", background: "#ff6600", border: "none" }}
                                        >
                                            <FiDollarSign size={16} /> {approving ? "Approving..." : "Approve ATH Payment"}
                                        </button>
                                    )}
                                </div>
                                <div>
                                    <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Shipping Address</p>
                                    {isEditing ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            {viewing.shippingLine1 ? (
                                                <>
                                                    <input value={editedData.shippingLine1} onChange={e => setEditedData({...editedData, shippingLine1: e.target.value})} placeholder="Line 1" style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6 }} />
                                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                                        <input value={editedData.shippingCity} onChange={e => setEditedData({...editedData, shippingCity: e.target.value})} placeholder="City" style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6 }} />
                                                        <input value={editedData.shippingState} onChange={e => setEditedData({...editedData, shippingState: e.target.value})} placeholder="State" style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6 }} />
                                                    </div>
                                                    <input value={editedData.shippingZip} onChange={e => setEditedData({...editedData, shippingZip: e.target.value})} placeholder="Zip" style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6 }} />
                                                </>
                                            ) : (
                                                <textarea value={editedData.address} onChange={e => setEditedData({...editedData, address: e.target.value})} style={{ padding: "6px 10px", fontSize: 13, border: "1.5px solid #eee", borderRadius: 6, minHeight: 80 }} />
                                            )}
                                        </div>
                                    ) : (
                                        <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                                            {viewing.shippingLine1 ? (
                                                <>{viewing.shippingLine1}<br />{viewing.shippingCity}, {viewing.shippingState} {viewing.shippingZip}<br />{viewing.shippingCountry}</>
                                            ) : (viewing.address || "—")}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Payment</p>
                                    <p style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "#555", margin: 0 }}>{viewing.paymentMethod || "—"}</p>
                                    <p style={{ fontSize: 12, color: "#888", margin: "4px 0 0" }}>
                                        {viewing.createdAt?.toDate ? viewing.createdAt.toDate().toLocaleString() : "—"}
                                    </p>
                                    {isEditing && (
                                        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                                            <button onClick={saveCustomerInfo} className="admin-btn-primary" style={{ padding: "6px 12px", fontSize: 12 }}>
                                                <FiCheck size={14} /> SAVE
                                            </button>
                                            <button onClick={() => setIsEditing(false)} className="admin-btn-secondary" style={{ padding: "6px 12px", fontSize: 12 }}>
                                                CANCEL
                                            </button>
                                        </div>
                                    )}
                                    {viewing.items?.some(i => i.isDigital || i.bundleDelivery === "Combo") && (
                                        <div style={{ marginTop: 16, padding: 12, background: "#f0f7ff", borderRadius: 8, border: "1px solid #cce5ff" }}>
                                            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#004085", fontWeight: 700 }}>DIGITAL CONTENT</p>
                                            <p style={{ margin: "0 0 12px", fontSize: 11, color: "#666" }}>
                                                Window: {viewing.downloadExpiresAt ? new Date(viewing.downloadExpiresAt).toLocaleString() : "Not set"}
                                            </p>
                                            <button 
                                                className="admin-btn-secondary" 
                                                onClick={() => resetDownloadWindow(viewing)}
                                                style={{ width: "100%", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px" }}
                                            >
                                                <FiRefreshCw size={12} /> RESET 1H WINDOW
                                            </button>
                                            <button 
                                                className="admin-btn-secondary" 
                                                onClick={() => handleResendEmail(viewing.id)}
                                                disabled={resendingEmail === viewing.id}
                                                style={{ width: "100%", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px", marginTop: 8, background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534" }}
                                            >
                                                <FiMail size={12} /> {resendingEmail === viewing.id ? "SENDING..." : "RESEND CONFIRMATION"}
                                            </button>
                                        </div>
                                    )}
                                    {!viewing.items?.some(i => i.isDigital || i.bundleDelivery === "Combo") && (
                                        <div style={{ marginTop: 16 }}>
                                            <button 
                                                className="admin-btn-secondary" 
                                                onClick={() => handleResendEmail(viewing.id)}
                                                disabled={resendingEmail === viewing.id}
                                                style={{ width: "100%", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px", background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534" }}
                                            >
                                                <FiMail size={12} /> {resendingEmail === viewing.id ? "SENDING..." : "RESEND CONFIRMATION"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Tracking */}
                            {!isEditing && (
                                <div style={{ background: "#f9f9f9", borderRadius: 12, padding: 20, marginBottom: 24 }}>
                                    <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                                        <FiTruck size={14} /> Tracking Number
                                    </p>
                                    <div style={{ display: "flex", gap: 12 }}>
                                        <select 
                                            value={editedData.carrier || viewing.carrier || "USPS"}
                                            onChange={e => setEditedData({...editedData, carrier: e.target.value})}
                                            style={{ padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e0ddd8", fontSize: 14 }}
                                        >
                                            <option value="USPS">USPS</option>
                                            <option value="UPS">UPS</option>
                                            <option value="FedEx">FedEx</option>
                                            <option value="DHL">DHL</option>
                                            <option value="Local Courier">Local Courier</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <input
                                            value={tracking}
                                            onChange={e => setTracking(e.target.value)}
                                            placeholder="e.g. 9400111899223432432..."
                                            style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e0ddd8", fontSize: 14 }}
                                        />
                                        <button className="admin-btn-primary" onClick={saveTracking} disabled={updatingTracking} style={{ whiteSpace: "nowrap" }}>
                                            {updatingTracking ? "Saving…" : "Save & Mark Shipped"}
                                        </button>
                                    </div>
                                    {viewing.trackingNumber && (
                                        <div style={{ marginTop: 12 }}>
                                            <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                                                Current: <strong>{viewing.carrier || editedData.carrier || "USPS"} - {viewing.trackingNumber}</strong> — customers can see this on their order tracking page.
                                            </p>
                                            <button 
                                                className="admin-btn-secondary"
                                                onClick={() => handleSendShippedEmail(viewing.id)}
                                                disabled={sendingShippedEmail}
                                                style={{ marginTop: 8, background: "#e0f2fe", color: "#0369a1", borderColor: "#bae6fd", fontSize: 12, padding: "6px 12px", display: "flex", alignItems: "center", gap: 6 }}
                                            >
                                                <FiMail size={14} /> {sendingShippedEmail ? "Sending..." : "Send Shipped Email"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Items */}
                            {isEditing ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 16, borderTop: "1.5px solid #eee", paddingTop: 20 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", margin: 0 }}>Edit Order Items</p>
                                    </div>

                                    {/* Search to add products */}
                                    <div style={{ position: "relative" }}>
                                        <FiSearch style={{ position: "absolute", left: 12, top: 12, color: "#aaa" }} />
                                        <input 
                                            placeholder="Search products to add..." 
                                            value={editSearchTerm}
                                            onChange={e => setEditSearchTerm(e.target.value)}
                                            style={{ width: "100%", padding: "10px 10px 10px 36px", borderRadius: 8, border: "1.5px solid #eee", outline: "none", fontSize: 13 }} 
                                        />
                                        {filteredEditProducts.length > 0 && (
                                            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", boxShadow: "0 10px 30px rgba(0,0,0,0.1)", borderRadius: 12, zIndex: 20, marginTop: 4, maxHeight: 200, overflowY: "auto", border: "1px solid #eee" }}>
                                                {filteredEditProducts.map(p => (
                                                    <div key={p.id} onClick={() => { addEditManualItem(p); setEditSearchTerm(""); }} style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f9f9f9", display: "flex", gap: 10, alignItems: "center" }}>
                                                        {p.imageUrl && <img src={p.imageUrl} style={{ width: 25, height: 35, objectFit: "cover", borderRadius: 4 }} />}
                                                        <div>
                                                            <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>{p.title}</p>
                                                            <p style={{ margin: 0, fontSize: 11, color: "#888" }}>${p.price}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Items List */}
                                    <div style={{ border: "1.5px solid #eee", borderRadius: 10, padding: 12, maxHeight: 200, overflowY: "auto" }}>
                                        {(!editedData.items || editedData.items.length === 0) ? (
                                            <p style={{ fontSize: 12, color: "#aaa", textAlign: "center", margin: "10px 0" }}>No items added yet</p>
                                        ) : (
                                            editedData.items.map((item, idx) => {
                                                const originalProduct = allProducts.find(prod => prod.id === item.id);
                                                const hasVariants = originalProduct?.variants && originalProduct.variants.length > 0;
                                                
                                                return (
                                                    <div key={idx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #f9f9f9" }}>
                                                        <div style={{ flex: 1 }}>
                                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{item.title}</p>
                                                            <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                                                                {hasVariants ? (
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                        <label style={{ fontSize: 10, color: "#888" }}>Format</label>
                                                                        <select 
                                                                            value={item.format || item.selectedFormat || ""} 
                                                                            onChange={e => {
                                                                                const selectedFormat = e.target.value;
                                                                                const variant = originalProduct.variants.find(v => v.format === selectedFormat);
                                                                                if (variant) {
                                                                                    const ni = [...editedData.items];
                                                                                    ni[idx].format = selectedFormat;
                                                                                    ni[idx].selectedFormat = selectedFormat;
                                                                                    ni[idx].price = variant.price || 0;
                                                                                    ni[idx].isDigital = selectedFormat === "eBook" || selectedFormat === "Audiobook";
                                                                                    ni[idx].epubStoragePath = variant.epubStoragePath || null;
                                                                                    updateEditTotals(ni, editedData.shippingCost);
                                                                                }
                                                                            }}
                                                                            style={{ padding: 4, fontSize: 11, borderRadius: 4, border: "1px solid #ddd", background: "white", minWidth: 100 }}
                                                                        >
                                                                            {originalProduct.variants.map(v => (
                                                                                <option key={v.format} value={v.format}>{v.format} (${v.price})</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                        <label style={{ fontSize: 10, color: "#888" }}>Format</label>
                                                                        <span style={{ fontSize: 11, padding: "4px 0", color: "#666" }}>{item.format || "Paperback"}</span>
                                                                    </div>
                                                                )}
                                                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                    <label style={{ fontSize: 10, color: "#888" }}>Price</label>
                                                                    <input 
                                                                        type="number" 
                                                                        value={item.price} 
                                                                        onChange={e => {
                                                                            const ni = [...editedData.items];
                                                                            ni[idx].price = parseFloat(e.target.value) || 0;
                                                                            updateEditTotals(ni, editedData.shippingCost);
                                                                        }} 
                                                                        style={{ width: 60, padding: 4, fontSize: 11, borderRadius: 4, border: "1px solid #ddd" }} 
                                                                    />
                                                                </div>
                                                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                    <label style={{ fontSize: 10, color: "#888" }}>Qty</label>
                                                                    <input 
                                                                        type="number" 
                                                                        value={item.quantity} 
                                                                        onChange={e => {
                                                                            const ni = [...editedData.items];
                                                                            ni[idx].quantity = parseInt(e.target.value) || 1;
                                                                            updateEditTotals(ni, editedData.shippingCost);
                                                                        }} 
                                                                        style={{ width: 45, padding: 4, fontSize: 11, borderRadius: 4, border: "1px solid #ddd" }} 
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button 
                                                            type="button"
                                                            onClick={() => {
                                                                const ni = editedData.items.filter((_, i) => i !== idx);
                                                                updateEditTotals(ni, editedData.shippingCost);
                                                            }} 
                                                            style={{ background: "none", border: "none", color: "#ff4d4d", cursor: "pointer", padding: 4 }}
                                                        >
                                                            <FiX size={16} />
                                                        </button>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Shipping Edit */}
                                    <div style={{ background: "#f8f9fa", padding: 12, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: 12, fontWeight: 600 }}>Shipping Cost</span>
                                        <input 
                                            type="number" 
                                            value={editedData.shippingCost} 
                                            onChange={e => updateEditTotals(editedData.items, e.target.value)} 
                                            style={{ width: 80, padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1.5px solid #ddd" }} 
                                        />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                        <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", margin: 0 }}>Order Items</p>
                                        <span style={{ fontSize: 11, color: "#888" }}>Select items to create a partial shipment</span>
                                    </div>
                                    
                                    {(viewing.items || []).map((item, i) => {
                                        const isDigital = item.isDigital;
                                        const isSelected = selectedItems.includes(i);
                                        const isShipped = viewing.shipments?.some(s => s.items.some(si => si.title === item.title && si.quantity === item.quantity)) || 
                                                         viewing.shipments?.some(s => s.items.some(si => si.itemIndex === i));
                                        
                                        return (
                                            <div key={i} style={{ 
                                                display: "flex", 
                                                alignItems: "center", 
                                                gap: 14, 
                                                padding: "10px 0", 
                                                borderBottom: "1px solid #f0f0f0",
                                                background: isSelected ? "#fffdf5" : "transparent",
                                                opacity: isShipped ? 0.7 : 1
                                            }}>
                                                {!isDigital && !isEditing && (
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isSelected}
                                                        disabled={isShipped}
                                                        onChange={() => {
                                                            if (isSelected) setSelectedItems(selectedItems.filter(idx => idx !== i));
                                                            else setSelectedItems([...selectedItems, i]);
                                                        }}
                                                        style={{ width: 18, height: 18, cursor: isShipped ? "not-allowed" : "pointer" }}
                                                    />
                                                )}
                                                {item.imageUrl && <img src={item.imageUrl} alt={item.title} style={{ width: 44, height: 56, objectFit: "cover", borderRadius: 6 }} />}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        <p style={{ fontWeight: 600, margin: 0 }}>{item.title}</p>
                                                        {isShipped && <span style={{ fontSize: 9, background: "#d1ecf1", color: "#0c5460", padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>SHIPPED</span>}
                                                    </div>
                                                    <p style={{ fontSize: 13, color: "#888", margin: 0 }}>Qty: {item.quantity} {isDigital && "(Digital)"}</p>
                                                    {isDigital && (() => {
                                                        const originalProduct = allProducts.find(p => p.id === item.id);
                                                        const ebookVariant = originalProduct?.variants?.find(v => v.format === "eBook");
                                                        const resolvedPath = item.epubStoragePath || ebookVariant?.epubStoragePath || originalProduct?.epubStoragePath;
                                                        
                                                        if (!resolvedPath) return null;
                                                        
                                                        const tokenKey = `${item.id}_${i}`;
                                                        const token = oneTimeTokens[tokenKey];
                                                        
                                                        return (
                                                            <div style={{ marginTop: 8 }}>
                                                                {token ? (
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 8, background: "#f8f9fa", padding: 12, borderRadius: 8, border: "1px solid #e9ecef", maxWidth: 300 }}>
                                                                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                                                            <img 
                                                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`${window.location.origin}/download-otd/${token}`)}`} 
                                                                                alt="One-Time QR" 
                                                                                style={{ width: 80, height: 80, border: "1px solid #ddd", background: "white", borderRadius: 4, padding: 4 }}
                                                                            />
                                                                            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                                                                                <span style={{ fontSize: 11, fontWeight: 700, color: "#0d4a35" }}>One-Time Link Ready</span>
                                                                                <input 
                                                                                    type="text" 
                                                                                    readOnly 
                                                                                    value={`${window.location.origin}/download-otd/${token}`} 
                                                                                    style={{ width: "100%", padding: "4px 8px", fontSize: 10, border: "1px solid #ccc", borderRadius: 4, background: "#fff", outline: "none" }} 
                                                                                    onClick={e => e.target.select()}
                                                                                />
                                                                                <button 
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        navigator.clipboard.writeText(`${window.location.origin}/download-otd/${token}`);
                                                                                        toast.success("One-time link copied!");
                                                                                    }}
                                                                                    style={{ background: "#e9ecef", border: "1px solid #ccc", borderRadius: 4, padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", color: "#333" }}
                                                                                >
                                                                                    Copy Link
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleGenerateOneTimeQR(item, i, resolvedPath)}
                                                                        disabled={generatingToken === tokenKey}
                                                                        style={{ 
                                                                            background: "var(--accent)", 
                                                                            color: "white", 
                                                                            border: "none", 
                                                                            borderRadius: 4, 
                                                                            padding: "4px 10px", 
                                                                            fontSize: 11, 
                                                                            fontWeight: 700, 
                                                                            cursor: "pointer" 
                                                                        }}
                                                                    >
                                                                        {generatingToken === tokenKey ? "Generating..." : "Generate One-Time Link & QR"}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                                <strong>${(item.price * item.quantity).toFixed(2)}</strong>
                                            </div>
                                        );
                                    })}

                                    {/* Create Shipment from Selection */}
                                    {!isEditing && selectedItems.length > 0 && (
                                        <div style={{ marginTop: 20, padding: 16, background: "#fff9db", borderRadius: 12, border: "1.5px solid #fab005" }}>
                                            <p style={{ fontSize: 13, fontWeight: 700, margin: "0 0 12px" }}>Create shipment for {selectedItems.length} selected item(s)</p>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                                <select 
                                                    value={editedData.carrier || "USPS"}
                                                    onChange={e => setEditedData({...editedData, carrier: e.target.value})}
                                                    style={{ padding: "8px 12px", borderRadius: 6, border: "1.5px solid #dee2e6", fontSize: 13 }}
                                                >
                                                    <option value="USPS">USPS</option>
                                                    <option value="UPS">UPS</option>
                                                    <option value="FedEx">FedEx</option>
                                                    <option value="DHL">DHL</option>
                                                    <option value="Local Courier">Local Courier</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                                <input 
                                                    value={shipmentTracking}
                                                    onChange={e => setShipmentTracking(e.target.value)}
                                                    placeholder="Enter tracking number..."
                                                    style={{ flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: 6, border: "1.5px solid #dee2e6" }}
                                                />
                                                <button 
                                                    className="admin-btn-primary" 
                                                    onClick={addShipment}
                                                    disabled={updatingTracking}
                                                    style={{ background: "#fab005", color: "#444", border: "none" }}
                                                >
                                                    {updatingTracking ? "Adding..." : "Add Shipment"}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Shipments List */}
                                    {!isEditing && viewing.shipments && viewing.shipments.length > 0 && (
                                        <div style={{ marginTop: 30 }}>
                                            <p style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>Shipments ({viewing.shipments.length})</p>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                                {viewing.shipments.map((ship, idx) => (
                                                    <div key={idx} style={{ padding: 16, background: "#f8f9fa", borderRadius: 12, border: "1px solid #e9ecef" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                                                            <div>
                                                                <p style={{ margin: 0, fontWeight: 700, color: "#1c7ed6" }}>
                                                                    <FiTruck size={14} style={{ marginRight: 6 }} />
                                                                    {ship.carrier}: {ship.trackingNumber}
                                                                </p>
                                                                <p style={{ margin: 0, fontSize: 11, color: "#888" }}>
                                                                    Shipped on {new Date(ship.shippedAt).toLocaleDateString()}
                                                                </p>
                                                            </div>
                                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                                <button 
                                                                    className="admin-btn-secondary"
                                                                    onClick={() => handleSendShippedEmail(viewing.id, ship)}
                                                                    disabled={sendingShippedEmail}
                                                                    style={{ background: "#e0f2fe", color: "#0369a1", borderColor: "#bae6fd", fontSize: 11, padding: "4px 8px", display: "flex", alignItems: "center", gap: 4 }}
                                                                >
                                                                    <FiMail size={12} /> {sendingShippedEmail ? "Sending..." : "Email Shipped"}
                                                                </button>
                                                                <button 
                                                                    onClick={() => deleteShipment(ship.id)}
                                                                    style={{ background: "none", border: "none", color: "#fa5252", cursor: "pointer", padding: 4 }}
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                                            {ship.items.map((si, sidx) => (
                                                                <div key={sidx} style={{ fontSize: 12, background: "white", padding: "4px 8px", borderRadius: 6, border: "1px solid #dee2e6" }}>
                                                                    {si.title} (x{si.quantity})
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                                {!isEditing ? (
                                    deleteConfirmId === viewing.id ? (
                                        <button 
                                            type="button"
                                            className="admin-btn-danger" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDelete(viewing.id);
                                            }}
                                            style={{ padding: "8px 16px", fontSize: 12, background: "#c0392b", color: "white" }}
                                        >
                                            CONFIRM DELETE?
                                        </button>
                                    ) : (
                                        <div style={{ display: "flex", gap: 10 }}>
                                            <button 
                                                type="button" 
                                                onClick={() => window.print()}
                                                className="admin-btn-secondary"
                                                style={{ padding: "8px 16px", fontSize: 12 }}
                                            >
                                                Print / PDF
                                            </button>
                                            <button 
                                                type="button"
                                                className="admin-btn-danger" 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setDeleteConfirmId(viewing.id);
                                                }}
                                                style={{ padding: "8px 16px", fontSize: 12 }}
                                            >
                                                <FiTrash2 size={14} /> DELETE ORDER
                                            </button>
                                        </div>
                                    )
                                ) : <div />}
                                <div style={{ textAlign: "right" }}>
                                    <p style={{ fontSize: 14, color: "#888", margin: "4px 0" }}>
                                        Subtotal: ${(isEditing ? (editedData.subtotal || 0) : (viewing.subtotal || 0)).toFixed(2)}
                                    </p>
                                    {viewing.discountAmount > 0 && (
                                        <p style={{ fontSize: 14, color: "#27ae60", margin: "4px 0" }}>
                                            Discount {viewing.appliedCoupon ? `(${viewing.appliedCoupon.code})` : ""}: -${(viewing.discountAmount || 0).toFixed(2)}
                                        </p>
                                    )}
                                    <p style={{ fontSize: 14, color: "#888", margin: "4px 0" }}>
                                        Shipping: {(isEditing ? (editedData.shippingCost || 0) : (viewing.shippingCost || 0)) === 0 ? "Free" : `$${(isEditing ? (editedData.shippingCost || 0) : (viewing.shippingCost || 0)).toFixed(2)}`}
                                        {!isEditing && viewing.shippingMethod && ` (${viewing.shippingMethod})`}
                                    </p>
                                    <p style={{ fontSize: 20, fontWeight: 700, color: "#0d4a35", marginTop: 12 }}>
                                        Total: ${(isEditing ? (editedData.total || 0) : (viewing.total || 0)).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Order Modal */}
            {creating && (
                <div className="admin-modal-backdrop" onClick={e => e.target === e.currentTarget && setCreating(false)}>
                    <div className="admin-modal" style={{ maxWidth: 900 }}>
                        <div className="admin-modal-header">
                            <h3>New Manual Order</h3>
                            <button className="admin-modal-close" onClick={() => setCreating(false)}><FiX size={20} /></button>
                        </div>
                        <div className="admin-modal-body admin-grid-2" style={{ gap: 24 }}>
                            {/* Left: Customer & Details */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                <p style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", color: "#888", margin: 0 }}>Customer Info</p>
                                <input value={manualForm.customerName} onChange={e => setManualForm({...manualForm, customerName: e.target.value})} placeholder="Full Name *" style={{ padding: 12, borderRadius: 8, border: "1.5px solid #eee" }} />
                                
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                    <input type="checkbox" id="reqEmail" checked={manualForm.requiresEmail} onChange={e => setManualForm({...manualForm, requiresEmail: e.target.checked})} />
                                    <label htmlFor="reqEmail" style={{ fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Requires Email Address?</label>
                                </div>

                                <input value={manualForm.email} onChange={e => setManualForm({...manualForm, email: e.target.value})} placeholder={manualForm.requiresEmail ? "Email Address *" : "Email Address (Optional)"} style={{ padding: 12, borderRadius: 8, border: "1.5px solid #eee" }} />
                                <input value={manualForm.phone} onChange={e => setManualForm({...manualForm, phone: e.target.value})} placeholder="Phone Number" style={{ padding: 12, borderRadius: 8, border: "1.5px solid #eee" }} />

                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                                    <input type="checkbox" id="reqShip" checked={manualForm.requiresShipping} onChange={e => setManualForm({...manualForm, requiresShipping: e.target.checked})} />
                                    <label htmlFor="reqShip" style={{ fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Requires Shipping Address?</label>
                                </div>

                                {manualForm.requiresShipping && (
                                    <>
                                        <p style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", color: "#888", margin: "8px 0 0" }}>Shipping Address</p>
                                        <input value={manualForm.shippingLine1} onChange={e => setManualForm({...manualForm, shippingLine1: e.target.value})} placeholder="Line 1" style={{ padding: 10, borderRadius: 8, border: "1.5px solid #eee" }} />
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                            <input value={manualForm.shippingCity} onChange={e => setManualForm({...manualForm, shippingCity: e.target.value})} placeholder="City" style={{ padding: 10, borderRadius: 8, border: "1.5px solid #eee" }} />
                                            <input value={manualForm.shippingState} onChange={e => setManualForm({...manualForm, shippingState: e.target.value})} placeholder="State" style={{ padding: 10, borderRadius: 8, border: "1.5px solid #eee" }} />
                                        </div>
                                        <div className="admin-grid-2" style={{ gap: 10 }}>
                                         <input value={manualForm.shippingZip} onChange={e => setManualForm({...manualForm, shippingZip: e.target.value})} placeholder="Zip" style={{ padding: 10, borderRadius: 8, border: "1.5px solid #eee" }} />
                                            <input value={manualForm.shippingCountry} onChange={e => setManualForm({...manualForm, shippingCountry: e.target.value})} placeholder="Country" style={{ padding: 10, borderRadius: 8, border: "1.5px solid #eee" }} />
                                        </div>
                                    </>
                                )}

                                <div style={{ marginTop: 8 }}>
                                    <p style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", color: "#888", margin: "0 0 8px" }}>Payment Method</p>
                                    <select 
                                        value={manualForm.paymentMethod} 
                                        onChange={e => setManualForm({...manualForm, paymentMethod: e.target.value})}
                                        style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #eee", fontSize: 14 }}
                                    >
                                        {MANUAL_PAYMENT_METHODS.map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Right: Items Selection */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                <p style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", color: "#888", margin: 0 }}>Add Products</p>
                                <div style={{ position: "relative" }}>
                                    <FiSearch style={{ position: "absolute", left: 12, top: 12, color: "#aaa" }} />
                                    <input 
                                        placeholder="Search products..." 
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        style={{ width: "100%", padding: "10px 10px 10px 36px", borderRadius: 8, border: "1.5px solid #eee", outline: "none" }} 
                                    />
                                    {filteredProducts.length > 0 && (
                                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", boxShadow: "0 10px 30px rgba(0,0,0,0.1)", borderRadius: 12, zIndex: 10, marginTop: 4, maxHeight: 250, overflowY: "auto", border: "1px solid #eee" }}>
                                            {filteredProducts.map(p => (
                                                <div key={p.id} onClick={() => { addManualItem(p); setSearchTerm(""); }} style={{ padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid #f9f9f9", display: "flex", gap: 10, alignItems: "center" }}>
                                                    {p.imageUrl && <img src={p.imageUrl} style={{ width: 30, height: 40, objectFit: "cover", borderRadius: 4 }} />}
                                                    <div>
                                                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{p.title}</p>
                                                        <p style={{ margin: 0, fontSize: 11, color: "#888" }}>${p.price}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ maxHeight: 200, overflowY: "auto", border: "1.5px solid #eee", borderRadius: 10, padding: 12 }}>
                                    {manualForm.items.length === 0 ? <p style={{ fontSize: 12, color: "#aaa", textAlign: "center", margin: "20px 0" }}>No items added yet</p> : (
                                        manualForm.items.map((item, idx) => {
                                            const originalProduct = allProducts.find(prod => prod.id === item.id);
                                            const hasVariants = originalProduct?.variants && originalProduct.variants.length > 0;
                                            
                                            return (
                                                <div key={idx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #f9f9f9" }}>
                                                    <div style={{ flex: 1 }}>
                                                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{item.title}</p>
                                                        <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                                                            {hasVariants ? (
                                                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                    <label style={{ fontSize: 10, color: "#888" }}>Format</label>
                                                                    <select 
                                                                        value={item.format || item.selectedFormat || ""} 
                                                                        onChange={e => {
                                                                            const selectedFormat = e.target.value;
                                                                            const variant = originalProduct.variants.find(v => v.format === selectedFormat);
                                                                            if (variant) {
                                                                                const ni = [...manualForm.items];
                                                                                ni[idx].format = selectedFormat;
                                                                                ni[idx].selectedFormat = selectedFormat; // backward-compat
                                                                                ni[idx].price = variant.price || 0;
                                                                                ni[idx].isDigital = selectedFormat === "eBook" || selectedFormat === "Audiobook";
                                                                                ni[idx].epubStoragePath = variant.epubStoragePath || null;
                                                                                updateTotals(ni, manualForm.shippingCost);
                                                                            }
                                                                        }}
                                                                        style={{ padding: 4, fontSize: 12, borderRadius: 4, border: "1px solid #ddd", background: "white", minWidth: 100 }}
                                                                    >
                                                                        {originalProduct.variants.map(v => (
                                                                            <option key={v.format} value={v.format}>{v.format} (${v.price})</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            ) : (
                                                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                    <label style={{ fontSize: 10, color: "#888" }}>Format</label>
                                                                    <span style={{ fontSize: 12, padding: "4px 0", color: "#666" }}>{item.format || "Paperback"}</span>
                                                                </div>
                                                            )}
                                                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                <label style={{ fontSize: 10, color: "#888" }}>Price</label>
                                                                <input type="number" value={item.price} onChange={e => {
                                                                    const ni = [...manualForm.items]; ni[idx].price = parseFloat(e.target.value) || 0;
                                                                    updateTotals(ni, manualForm.shippingCost);
                                                                }} style={{ width: 70, padding: 4, fontSize: 12, borderRadius: 4, border: "1px solid #ddd" }} />
                                                            </div>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                <label style={{ fontSize: 10, color: "#888" }}>Qty</label>
                                                                <input type="number" value={item.quantity} onChange={e => {
                                                                    const ni = [...manualForm.items]; ni[idx].quantity = parseInt(e.target.value) || 1;
                                                                    updateTotals(ni, manualForm.shippingCost);
                                                                }} style={{ width: 50, padding: 4, fontSize: 12, borderRadius: 4, border: "1px solid #ddd" }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => {
                                                        const ni = manualForm.items.filter((_, i) => i !== idx);
                                                        updateTotals(ni, manualForm.shippingCost);
                                                    }} style={{ background: "none", border: "none", color: "#ff4d4d", cursor: "pointer" }}><FiX size={16} /></button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                <div style={{ background: "#f8f9fa", padding: 16, borderRadius: 12 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                                        <span>Subtotal</span>
                                        <span>${manualForm.subtotal.toFixed(2)}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, alignItems: "center", marginBottom: 12 }}>
                                        <span>Shipping Cost</span>
                                        <input type="number" value={manualForm.shippingCost} onChange={e => updateTotals(manualForm.items, e.target.value)} style={{ width: 80, padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1.5px solid #ddd" }} />
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 800, color: "var(--green-primary)", borderTop: "1.5px solid #eee", paddingTop: 12 }}>
                                        <span>Total</span>
                                        <span>${manualForm.total.toFixed(2)}</span>
                                    </div>
                                </div>

                                <button 
                                    className="admin-btn-primary" 
                                    onClick={handleCreateManualOrder}
                                    style={{ padding: 16, borderRadius: 12, marginTop: 8 }}
                                >
                                    {loading ? "Creating..." : "Create Order & Get Link"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <>
                    <style>{`
                        @media print {
                            body {
                                background: white !important;
                                color: black !important;
                                font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
                            }
                            /* Hide all dashboard structure, modals, headers, and sidebars */
                            .admin-shell, .admin-sidebar, .admin-topbar, .admin-modal-backdrop, .admin-modal, .admin-stats, .admin-section-header, .admin-activity-banner, .admin-card, .admin-table-wrap, .admin-empty, button, .admin-btn-primary, .admin-btn-secondary, select, input {
                                display: none !important;
                            }
                            /* Reset layout wrappers to standard block flow */
                            .admin-shell, .admin-main, .admin-content, #root {
                                display: block !important;
                                padding: 0 !important;
                                margin: 0 !important;
                                background: transparent !important;
                                border: none !important;
                                box-shadow: none !important;
                            }
                            .printable-invoice {
                                display: block !important;
                                width: 100% !important;
                                max-width: 800px !important;
                                margin: 0 auto !important;
                                padding: 20px !important;
                                background: white !important;
                                color: black !important;
                            }
                        }
                        @media screen {
                            .printable-invoice {
                                display: none !important;
                            }
                        }
                    `}</style>
                    <div className="printable-invoice">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #333", paddingBottom: 20, marginBottom: 20 }}>
                            <div>
                                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#111" }}>LB Books</h1>
                                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#666" }}>Order Invoice</p>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#555" }}>INVOICE</h2>
                                <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 600 }}>Order ID: #{viewing.id}</p>
                                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#666" }}>
                                    Date: {(() => {
                                        const dVal = viewing.createdAt;
                                        if (!dVal) return new Date().toLocaleDateString();
                                        if (dVal.toDate) return dVal.toDate().toLocaleDateString();
                                        if (dVal.seconds) return new Date(dVal.seconds * 1000).toLocaleDateString();
                                        return new Date(dVal).toLocaleDateString();
                                    })()}
                                </p>
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, marginBottom: 30 }}>
                            <div>
                                <h3 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: 0.5 }}>Customer Details</h3>
                                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#333" }}>{viewing.customerName || "N/A"}</p>
                                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#555" }}>Email: {viewing.email || "N/A"}</p>
                                <p style={{ margin: "2px 0 0", fontSize: 13, color: "#555" }}>Phone: {viewing.phone || "N/A"}</p>
                            </div>
                            <div>
                                {viewing.shippingLine1 ? (
                                    <div>
                                        <h3 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: 0.5 }}>Shipping Address</h3>
                                        <p style={{ margin: 0, fontSize: 13, color: "#333", lineHeight: 1.5 }}>
                                            {viewing.shippingLine1}<br />
                                            {viewing.shippingCity}, {viewing.shippingState} {viewing.shippingZip}<br />
                                            {viewing.shippingCountry}
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        <h3 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: 0.5 }}>Billing/Shipping Address</h3>
                                        <p style={{ margin: 0, fontSize: 13, color: "#333", lineHeight: 1.5 }}>
                                            {viewing.address || "Digital Delivery / No Shipping"}
                                        </p>
                                    </div>
                                )}
                                <div style={{ marginTop: 12 }}>
                                    <span style={{ fontSize: 12, color: "#888", fontWeight: 700, textTransform: "uppercase", marginRight: 6 }}>Payment Method:</span>
                                    <strong style={{ fontSize: 13, color: "#333" }}>{viewing.paymentMethod || "N/A"}</strong>
                                </div>
                            </div>
                        </div>

                        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 30 }}>
                            <thead>
                                <tr style={{ borderBottom: "2px solid #eee", textAlign: "left" }}>
                                    <th style={{ padding: "10px 0", fontSize: 13, fontWeight: 700, color: "#666" }}>Product Description</th>
                                    <th style={{ padding: "10px 0", fontSize: 13, fontWeight: 700, color: "#666" }}>Format</th>
                                    <th style={{ padding: "10px 0", fontSize: 13, fontWeight: 700, color: "#666", textAlign: "right" }}>Price</th>
                                    <th style={{ padding: "10px 0", fontSize: 13, fontWeight: 700, color: "#666", textAlign: "center" }}>Qty</th>
                                    <th style={{ padding: "10px 0", fontSize: 13, fontWeight: 700, color: "#666", textAlign: "right" }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(viewing.items || []).map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "12px 0", fontSize: 13, fontWeight: 600, color: "#333" }}>{item.title}</td>
                                        <td style={{ padding: "12px 0", fontSize: 13, color: "#555" }}>{item.format || (item.isDigital ? "eBook" : "Paperback")}</td>
                                        <td style={{ padding: "12px 0", fontSize: 13, color: "#555", textAlign: "right" }}>${(item.price || 0).toFixed(2)}</td>
                                        <td style={{ padding: "12px 0", fontSize: 13, color: "#555", textAlign: "center" }}>{item.quantity}</td>
                                        <td style={{ padding: "12px 0", fontSize: 13, fontWeight: 600, color: "#333", textAlign: "right" }}>${((item.price || 0) * item.quantity).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <div style={{ width: 250 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, color: "#666" }}>
                                    <span>Subtotal:</span>
                                    <span>${(viewing.subtotal || 0).toFixed(2)}</span>
                                </div>
                                {viewing.discountAmount > 0 && (
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, color: "#27ae60", fontWeight: 600 }}>
                                        <span>Discount {viewing.appliedCoupon ? `(${viewing.appliedCoupon.code})` : ""}:</span>
                                        <span>-${(viewing.discountAmount || 0).toFixed(2)}</span>
                                    </div>
                                )}
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, color: "#666" }}>
                                    <span>Shipping:</span>
                                    <span>{viewing.shippingCost === 0 ? "Free" : `$${(viewing.shippingCost || 0).toFixed(2)}`}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", marginTop: 10, borderTop: "2px solid #333", fontSize: 16, fontWeight: 800, color: "#111" }}>
                                    <span>Total:</span>
                                    <span>${(viewing.total || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ borderTop: "1px dashed #ccc", marginTop: 50, paddingTop: 20, textAlign: "center" }}>
                            <p style={{ margin: 0, fontSize: 12, color: "#888" }}>Thank you for shopping with LB Books!</p>
                            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#aaa" }}>If you have any questions about this invoice, please contact support.</p>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

// Sub-component to avoid code repetition
function OrderTable({ list, updateStatus, setViewing, setTracking, setIsEditing, handleDelete, deleting, deleteConfirmId, setDeleteConfirmId, STATUS_COLORS, STATUS_OPTIONS, handleResendEmail, resendingEmail }) {
    if (list.length === 0) return <div className="admin-empty" style={{ padding: 40 }}><p>No orders matching your criteria.</p></div>;
    
    return (
        <div className="admin-table-wrap">
        <table className="admin-table">
            <thead>
                <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Action</th>
                </tr>
            </thead>
            <tbody>
                {list.map(o => (
                    <tr key={o.id}>
                        <td>
                            <code style={{ fontSize: 12, color: "#888" }}>#{o.id}</code>
                            <br />
                            <a 
                                href={`${window.location.origin}/order/${o.id}`} 
                                target="_blank" 
                                rel="noreferrer" 
                                style={{ fontSize: 11, color: "var(--accent)", textDecoration: "underline" }}
                            >
                                Public Link
                            </a>
                        </td>
                        <td>
                            <strong>{o.customerName || "—"}</strong><br />
                            <span style={{ fontSize: 12, color: "#888" }}>{o.email || "—"}</span>
                        </td>
                        <td style={{ fontSize: 13, color: "#888" }}>
                            {o.createdAt?.toDate ? o.createdAt.toDate().toLocaleDateString() : "—"}
                        </td>
                        <td>{o.items?.length ?? 0}</td>
                        <td><strong>${(o.total || 0).toFixed(2)}</strong></td>
                        <td>
                            <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", color: "#555", background: "#f4f5f7", padding: "3px 8px", borderRadius: 6 }}>
                                {o.paymentMethod || "—"}
                            </span>
                        </td>
                        <td>
                            <select
                                value={o.status || "pending"}
                                onChange={e => updateStatus(o.id, e.target.value)}
                                style={{
                                    padding: "5px 10px", borderRadius: 6, border: "1.5px solid #e0ddd8",
                                    fontSize: 12, fontWeight: 700, outline: "none", cursor: "pointer",
                                    background: STATUS_COLORS[o.status]?.bg || "#f4f5f7",
                                    color: STATUS_COLORS[o.status]?.color || "#333",
                                }}
                            >
                                {STATUS_OPTIONS.map(s => (
                                    <option key={s} value={s}>{s.replace(/_/g, ' ').charAt(0).toUpperCase() + s.replace(/_/g, ' ').slice(1)}</option>
                                ))}
                            </select>
                        </td>
                        <td style={{ display: "flex", gap: 8 }}>
                            <button type="button" className="admin-btn-secondary" style={{ padding: "6px 12px" }}
                                onClick={() => { setViewing(o); setTracking(o.trackingNumber || ""); setIsEditing(false); }}>
                                <FiEye size={14} />
                            </button>
                            <button type="button" className="admin-btn-secondary" style={{ padding: "6px 12px", background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534" }}
                                onClick={() => handleResendEmail(o.id)}
                                disabled={resendingEmail === o.id}
                                title="Resend Confirmation Email">
                                <FiMail size={14} />
                            </button>
                            
                            {deleteConfirmId === o.id ? (
                                <button 
                                    type="button"
                                    className="admin-btn-danger" 
                                    style={{ padding: "6px 10px", background: "#c0392b", color: "white", fontSize: 11 }}
                                    disabled={deleting}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDelete(o.id);
                                    }}>
                                    CONFIRM?
                                </button>
                            ) : (
                                <button 
                                    type="button"
                                    className="admin-btn-danger" 
                                    style={{ padding: "6px 10px" }}
                                    disabled={deleting}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setDeleteConfirmId(o.id);
                                    }}>
                                    <FiTrash2 size={14} />
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        </div>
    );
}
